import { useCreate, useOne, useUpdate } from '@refinedev/core';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { ui } from '../ui';

/**
 * Criar e editar série.
 *
 * O agendamento é o campo que mais importa e o que mais engana: `published_at` no futuro
 * significa **agendada** — a série existe no admin e não aparece no site até a hora. É
 * `episode_is_live()` no banco que faz valer, não o cliente. O formulário diz isso em
 * palavras, porque "data de publicação" vazia e "data no futuro" são coisas muito diferentes
 * e a diferença não é óbvia olhando o campo.
 *
 * Os temas são os mesmos rótulos que as categorias de Descobrir filtram (`docs/01`). Lista
 * fechada, não texto livre: um tema escrito diferente não aparece em filtro nenhum, e o erro
 * só seria notado quando a categoria viesse vazia.
 */
const TEMAS = [
  'Traição',
  'Vingança',
  'Segredo de família',
  'Amor proibido',
  'Suspense',
  'Virada de vida',
] as const;

const CLASSIFICACOES = ['L', '10', '12', '14', '16', '18'] as const;

type Campos = {
  slug: string;
  title: string;
  hook: string;
  caption: string;
  themes: string[];
  age_rating: string;
  feed_order: string;
  is_premiere: boolean;
  published_at: string;
};

const VAZIO: Campos = {
  slug: '',
  title: '',
  hook: '',
  caption: '',
  themes: [],
  age_rating: '14',
  feed_order: '',
  is_premiere: false,
  published_at: '',
};

/** `2026-10-09T14:30:00Z` → `2026-10-09T11:30` (o que o campo datetime-local espera). */
function paraCampoLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const deslocado = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
  return deslocado.toISOString().slice(0, 16);
}

export function SerieForm() {
  const { id } = useParams<{ id: string }>();
  const navegar = useNavigate();
  const editando = Boolean(id);

  const [campos, setCampos] = useState<Campos>(VAZIO);
  const [erro, setErro] = useState<string | null>(null);

  const { result: serie } = useOne<Record<string, unknown>>({
    resource: 'series',
    id: id ?? '',
    queryOptions: { enabled: editando },
  });

  const { mutate: criar, mutation: criacao } = useCreate();
  const { mutate: atualizar, mutation: atualizacao } = useUpdate();
  const salvando = criacao.isPending || atualizacao.isPending;

  useEffect(() => {
    if (!serie) return;
    setCampos({
      slug: String(serie['slug'] ?? ''),
      title: String(serie['title'] ?? ''),
      hook: String(serie['hook'] ?? ''),
      caption: String(serie['caption'] ?? ''),
      themes: (serie['themes'] as string[] | null) ?? [],
      age_rating: String(serie['age_rating'] ?? '14'),
      feed_order: serie['feed_order'] === null ? '' : String(serie['feed_order']),
      is_premiere: Boolean(serie['is_premiere']),
      published_at: paraCampoLocal((serie['published_at'] as string | null) ?? null),
    });
  }, [serie]);

  function enviar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);

    // O banco exige `^[a-z0-9-]{2,60}$`. Avisar aqui evita uma viagem até o servidor para
    // receber de volta um erro de constraint que ninguém entende.
    if (!/^[a-z0-9-]{2,60}$/.test(campos.slug)) {
      setErro('O endereço só aceita letras minúsculas, números e hífen (2 a 60 caracteres).');
      return;
    }

    const valores = {
      slug: campos.slug,
      title: campos.title,
      hook: campos.hook,
      caption: campos.caption || null,
      themes: campos.themes,
      age_rating: campos.age_rating,
      feed_order: campos.feed_order === '' ? null : Number(campos.feed_order),
      is_premiere: campos.is_premiere,
      published_at: campos.published_at ? new Date(campos.published_at).toISOString() : null,
    };

    const aoDarCerto = { onSuccess: () => navegar('/series') };
    const aoDarErro = {
      onError: (e2: { message?: string }) => setErro(e2.message ?? 'Não deu para salvar.'),
    };

    if (editando && id) {
      atualizar({ resource: 'series', id, values: valores }, { ...aoDarCerto, ...aoDarErro });
    } else {
      criar({ resource: 'series', values: valores }, { ...aoDarCerto, ...aoDarErro });
    }
  }

  function alternarTema(tema: string) {
    setCampos((c) => ({
      ...c,
      themes: c.themes.includes(tema) ? c.themes.filter((t) => t !== tema) : [...c.themes, tema],
    }));
  }

  return (
    <form onSubmit={enviar} style={{ maxWidth: 620 }}>
      <h1 style={ui.h1}>{editando ? 'Editar série' : 'Nova série'}</h1>

      <div style={ui.grupo}>
        <label style={ui.rotulo} htmlFor="title">
          Título
        </label>
        <input
          id="title"
          required
          value={campos.title}
          onChange={(e) => setCampos({ ...campos, title: e.target.value })}
          style={ui.campo}
        />
      </div>

      <div style={ui.grupo}>
        <label style={ui.rotulo} htmlFor="slug">
          Endereço
        </label>
        <input
          id="slug"
          required
          value={campos.slug}
          onChange={(e) => setCampos({ ...campos, slug: e.target.value })}
          style={ui.campo}
          placeholder="a-diarista"
        />
        <p style={ui.ajuda}>
          Vira o link compartilhado: venove.com.br/s/<strong>{campos.slug || 'a-diarista'}</strong>
          /1. Depois de publicada, mudar isto quebra os links que já circularam.
        </p>
      </div>

      <div style={ui.grupo}>
        <label style={ui.rotulo} htmlFor="hook">
          Gancho
        </label>
        <textarea
          id="hook"
          required
          rows={3}
          value={campos.hook}
          onChange={(e) => setCampos({ ...campos, hook: e.target.value })}
          style={ui.campo}
        />
        <p style={ui.ajuda}>A frase que aparece na página da série e na prévia do link.</p>
      </div>

      <div style={ui.grupo}>
        <label style={ui.rotulo} htmlFor="caption">
          Legenda do feed
        </label>
        <input
          id="caption"
          value={campos.caption}
          onChange={(e) => setCampos({ ...campos, caption: e.target.value })}
          style={ui.campo}
        />
        <p style={ui.ajuda}>O texto curto sobre o vídeo no feed. Vazio usa o gancho.</p>
      </div>

      <div style={ui.grupo}>
        <span style={ui.rotulo}>Temas</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {TEMAS.map((tema) => {
            const marcado = campos.themes.includes(tema);
            return (
              <button
                key={tema}
                type="button"
                onClick={() => alternarTema(tema)}
                aria-pressed={marcado}
                style={marcado ? ui.botao : ui.botaoSecundario}
              >
                {tema}
              </button>
            );
          })}
        </div>
        <p style={ui.ajuda}>São as categorias de Descobrir. Lista fechada, de propósito.</p>
      </div>

      <div style={{ ...ui.grupo, display: 'flex', gap: 16 }}>
        <div style={{ flex: 1 }}>
          <label style={ui.rotulo} htmlFor="age">
            Classificação
          </label>
          <select
            id="age"
            value={campos.age_rating}
            onChange={(e) => setCampos({ ...campos, age_rating: e.target.value })}
            style={ui.campo}
          >
            {CLASSIFICACOES.map((c) => (
              <option key={c} value={c}>
                {c === 'L' ? 'Livre' : `${c} anos`}
              </option>
            ))}
          </select>
        </div>

        <div style={{ flex: 1 }}>
          <label style={ui.rotulo} htmlFor="ordem">
            Ordem no feed
          </label>
          <input
            id="ordem"
            type="number"
            min={1}
            value={campos.feed_order}
            onChange={(e) => setCampos({ ...campos, feed_order: e.target.value })}
            style={ui.campo}
          />
          <p style={ui.ajuda}>Menor aparece antes. Vazio joga para o fim.</p>
        </div>
      </div>

      <div style={ui.grupo}>
        <label style={ui.rotulo} htmlFor="publicacao">
          Publicação
        </label>
        <input
          id="publicacao"
          type="datetime-local"
          value={campos.published_at}
          onChange={(e) => setCampos({ ...campos, published_at: e.target.value })}
          style={ui.campo}
        />
        <p style={ui.ajuda}>
          Vazio: rascunho, não aparece no site. Data no futuro: agendada, entra sozinha na hora
          marcada. Data no passado: já está no ar. Quem decide é o banco, não esta tela.
        </p>
      </div>

      <div style={ui.grupo}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
          <input
            type="checkbox"
            checked={campos.is_premiere}
            onChange={(e) => setCampos({ ...campos, is_premiere: e.target.checked })}
          />
          Destacar como &quot;Estreia hoje&quot; em Descobrir
        </label>
      </div>

      {erro && <p style={ui.erro}>{erro}</p>}

      <div style={ui.linhaBotoes}>
        <button type="submit" disabled={salvando} style={ui.botao}>
          {salvando ? 'Salvando…' : 'Salvar'}
        </button>
        <button type="button" onClick={() => navegar('/series')} style={ui.botaoSecundario}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
